import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRxCollection } from 'rxdb/plugins/react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import DialogFooterBar from '../../components/DialogFooterBar';
import FormDialog from '../../components/FormDialog';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import FormSection from '../../components/FormSection';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import GestureIcon from '@mui/icons-material/Gesture';
import CloseIcon from '@mui/icons-material/Close';
import AddLinkIcon from '@mui/icons-material/AddLink';
import type {
    MediaDocType,
    MediaLinkDocType,
    MediaOwnerType,
    OverallDescriptionDocType,
    AreaDescriptionDocType,
    DiaryDocType,
    SampleDocType
} from '@ast-data/shared';
import { useFamilyDocs } from '../../db/useScopedDocs';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useFamilyDescriptions } from '../findings/useFamilyDescriptions';
import { planumRefLabel, profileRefLabel } from '../findings/findingRefLabels';
import { locationLabel } from '../findings/locationLabel';
import { sampleLabel } from '../findings/sampleLabel';
import { areaDescriptionLocation } from '../findings/areaDescriptionLabel';
import { compareNatural } from '../../utils/compare';
import { useDiaryLabel } from '../findings/useDiaryLabel';
import { useMediaThumbUrl } from './useMediaBlobUrl';
import { useDocs } from './useOwnerMedia';
import {
    coveredPhotoIds,
    mediaLinkId,
    mediaShownBy,
    withBackgroundPhotos
} from './mediaLinks';

/**
 * What the first picker chooses: a Befund, a Gesamt- or Flächenbeschreibung, a
 * Probe or a Tagebuch day.
 */
interface SourceOption {
    kind:
        | 'finding'
        | 'overallDescription'
        | 'areaDescription'
        | 'sample'
        | 'diary';
    id: string;
    label: string;
}

/** What the second picker narrows a Befund to: one Planum or Profil. */
interface DescriptionOption {
    id: string;
    label: string;
}

/** A picture on offer, with the place it is shown in. */
interface Candidate {
    media: MediaDocType;
    placeLabel: string;
}

const TILE_HEIGHT = 130;
/** The name and place under each picture: two lines, fixed so a row is too. */
const TILE_LABEL_HEIGHT = 52;
/**
 * The picture area never shrinks below one row of tiles (plus their border),
 * so the dialog keeps its height while the choice above it changes.
 */
const TILE_ROW_HEIGHT = TILE_HEIGHT + TILE_LABEL_HEIGHT + 2;

/**
 * Pick photos and sketches from another description of the family and show
 * them here too — linked, not copied: the same picture, edited in one place,
 * changes in every place.
 */
export default function MediaLinkDialog({
    open,
    onClose,
    projectId,
    ownerType,
    ownerId,
    shownIds
}: {
    open: boolean;
    onClose: () => void;
    projectId: string;
    ownerType: MediaOwnerType;
    ownerId: string;
    /** Pictures this description shows already, own or linked. */
    shownIds: ReadonlySet<string>;
}) {
    const intl = useIntl();
    const linkCollection = useRxCollection<MediaLinkDocType>('media_links');
    const familyIds = useFamilyProjectIds(projectId);
    const { findings, planums, profiles } = useFamilyDescriptions(projectId);
    const { results: overallDescriptions } =
        useFamilyDocs<OverallDescriptionDocType>(
            'overall_descriptions',
            familyIds
        );
    const { results: areaDescriptions } = useFamilyDocs<AreaDescriptionDocType>(
        'area_descriptions',
        familyIds
    );
    const { results: samples } = useFamilyDocs<SampleDocType>(
        'samples',
        familyIds
    );
    const { results: diaries } = useFamilyDocs<DiaryDocType>(
        'diaries',
        familyIds
    );
    const diaryLabel = useDiaryLabel(projectId, familyIds);

    const [source, setSource] = useState<SourceOption | null>(null);
    const [description, setDescription] = useState<DescriptionOption | null>(
        null
    );
    const [selected, setSelected] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const sourceOptions = useMemo<SourceOption[]>(() => {
        const findingOptions = findings
            .filter((finding) => finding.findingNumber)
            .map<SourceOption>((finding) => ({
                kind: 'finding',
                id: finding.id,
                label: locationLabel({ findingNumber: finding.findingNumber })
            }))
            .sort((firstOption, secondOption) =>
                compareNatural(firstOption.label, secondOption.label)
            );
        const descriptionOptions = overallDescriptions
            .map<SourceOption>((overall) => ({
                kind: 'overallDescription',
                id: overall.id,
                label: intl.formatMessage(
                    { id: 'overallDescription.label' },
                    { number: overall.overallDescriptionNumber }
                )
            }))
            .sort((firstOption, secondOption) =>
                compareNatural(firstOption.label, secondOption.label)
            );
        const areaOptions = areaDescriptions
            .map<SourceOption>((area) => ({
                kind: 'areaDescription',
                id: area.id,
                label: intl.formatMessage(
                    { id: 'areaDescription.label' },
                    { location: areaDescriptionLocation(area) }
                )
            }))
            .sort((firstOption, secondOption) =>
                compareNatural(firstOption.label, secondOption.label)
            );
        const sampleOptions = samples
            .map<SourceOption>((sample) => ({
                kind: 'sample',
                id: sample.id,
                label: sampleLabel(intl, sample)
            }))
            .sort((firstOption, secondOption) =>
                compareNatural(firstOption.label, secondOption.label)
            );
        // Newest day first, as in the Tagebuch grid.
        const diaryOptions = [...diaries]
            .sort((firstDiary, secondDiary) =>
                secondDiary.recordedAt.localeCompare(firstDiary.recordedAt)
            )
            .map<SourceOption>((diary) => ({
                kind: 'diary',
                id: diary.id,
                label: diaryLabel(diary)
            }));
        return [
            ...findingOptions,
            ...descriptionOptions,
            ...areaOptions,
            ...sampleOptions,
            ...diaryOptions
        ];
    }, [
        findings,
        overallDescriptions,
        areaDescriptions,
        samples,
        diaries,
        diaryLabel,
        intl
    ]);

    const findingNumber =
        source?.kind === 'finding'
            ? (findings.find((finding) => finding.id === source.id)
                  ?.findingNumber ?? '')
            : '';

    /** Label per owner id within the chosen source. */
    const placeLabels = useMemo(() => {
        const labels = new Map<string, string>();
        if (!source) {
            return labels;
        }
        labels.set(source.id, source.label);
        if (source.kind === 'finding') {
            for (const planum of planums) {
                if (planum.findingId === source.id) {
                    labels.set(
                        planum.id,
                        planumRefLabel(findingNumber, planum)
                    );
                }
            }
            for (const profile of profiles) {
                if (profile.findingId === source.id) {
                    labels.set(
                        profile.id,
                        profileRefLabel(findingNumber, profile)
                    );
                }
            }
        }
        return labels;
    }, [source, planums, profiles, findingNumber]);

    const descriptionOptions = useMemo<DescriptionOption[]>(
        () =>
            [...placeLabels.entries()]
                .filter(([id]) => id !== source?.id)
                .map(([id, label]) => ({ id, label }))
                .sort((firstOption, secondOption) =>
                    compareNatural(firstOption.label, secondOption.label)
                ),
        [placeLabels, source]
    );

    /** The owners whose pictures are on offer, in label order, never this one. */
    const scopeIds = useMemo(() => {
        if (!source) {
            return [];
        }
        const ids = description
            ? [description.id]
            : [source.id, ...descriptionOptions.map((option) => option.id)];
        return ids.filter((id) => id !== ownerId);
    }, [source, description, descriptionOptions, ownerId]);

    const scopeMedia = useDocs<MediaDocType>('media', {
        ownerId: { $in: scopeIds }
    });
    const scopeLinks = useDocs<MediaLinkDocType>('media_links', {
        ownerId: { $in: scopeIds }
    });
    const scopeLinkedMedia = useDocs<MediaDocType>('media', {
        id: { $in: scopeLinks.map((link) => link.mediaId) }
    });

    const mediaById = useMemo(
        () =>
            new Map(
                [...scopeMedia, ...scopeLinkedMedia].map((media) => [
                    media.id,
                    media
                ])
            ),
        [scopeMedia, scopeLinkedMedia]
    );

    // Each place as its own gallery shows it — a photo under a sketch is
    // offered as that sketch — and each picture once, however many of the
    // places show it.
    const candidates = useMemo<Candidate[]>(() => {
        const seen = new Set<string>();
        const offered: Candidate[] = [];
        for (const placeId of scopeIds) {
            const shown = mediaShownBy(
                scopeMedia.filter((media) => media.ownerId === placeId),
                scopeLinks.filter((link) => link.ownerId === placeId),
                scopeLinkedMedia
            ).map((item) => item.media);
            const covered = coveredPhotoIds(shown);
            for (const media of shown) {
                if (
                    covered.has(media.id) ||
                    seen.has(media.id) ||
                    shownIds.has(media.id)
                ) {
                    continue;
                }
                seen.add(media.id);
                offered.push({
                    media,
                    placeLabel: placeLabels.get(placeId) ?? ''
                });
            }
        }
        return offered;
    }, [
        scopeIds,
        scopeMedia,
        scopeLinks,
        scopeLinkedMedia,
        shownIds,
        placeLabels
    ]);

    function toggle(id: string) {
        setSelected((current) =>
            current.includes(id)
                ? current.filter((selectedId) => selectedId !== id)
                : [...current, id]
        );
    }

    function close() {
        setSource(null);
        setDescription(null);
        setSelected([]);
        onClose();
    }

    async function confirm() {
        if (!linkCollection) {
            return;
        }
        setSaving(true);
        try {
            const now = new Date().toISOString();
            const ids = withBackgroundPhotos(selected, mediaById).filter(
                (id) => !shownIds.has(id)
            );
            for (const mediaId of ids) {
                // An upsert, not an insert: unlinked once, the same id is a
                // tombstone and comes back to life.
                await linkCollection.upsert({
                    id: mediaLinkId(mediaId, ownerId),
                    projectId,
                    mediaId,
                    ownerType,
                    ownerId,
                    createdBy: '',
                    updatedBy: '',
                    updatedAt: now,
                    createdAt: now
                });
            }
            close();
        } finally {
            setSaving(false);
        }
    }

    return (
        <FormDialog open={open} onClose={close} fullScreen>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                <FormDialogAppBar
                    // No project crumb: the dialog sits on top of a record
                    // dialog that names it already — title and close only.
                    projectId=""
                    onClose={close}
                    title={<FormattedMessage id="media.link.title" />}
                />
                {/* The record forms' canvas, but the filters stay put and
                        only the box of pictures scrolls. */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        bgcolor: 'background.default',
                        px: { xs: 0, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        display: 'flex'
                    }}
                >
                    <Stack
                        spacing={{ xs: 2, sm: 3 }}
                        sx={{
                            maxWidth: 800,
                            mx: 'auto',
                            width: '100%',
                            minHeight: 0
                        }}
                    >
                        <FormSection sx={{ flexShrink: 0 }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                            >
                                <Autocomplete
                                    sx={{ flex: 1 }}
                                    options={sourceOptions}
                                    value={source}
                                    groupBy={(option) =>
                                        intl.formatMessage({
                                            id: `media.link.group.${option.kind}`
                                        })
                                    }
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    onChange={(_event, value) => {
                                        setSource(value);
                                        setDescription(null);
                                        setSelected([]);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <FormattedMessage id="media.link.source" />
                                            }
                                        />
                                    )}
                                />
                                <Autocomplete
                                    sx={{ flex: 1 }}
                                    options={descriptionOptions}
                                    value={description}
                                    disabled={source?.kind !== 'finding'}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    onChange={(_event, value) => {
                                        setDescription(value);
                                        setSelected([]);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={
                                                <FormattedMessage id="media.link.description" />
                                            }
                                            placeholder={intl.formatMessage({
                                                id: 'media.link.allDescriptions'
                                            })}
                                        />
                                    )}
                                />
                            </Stack>
                        </FormSection>
                        <FormSection
                            sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
                        >
                            <Box
                                sx={{
                                    minHeight: TILE_ROW_HEIGHT,
                                    // Columns, not fixed tiles: two on a phone, three from
                                    // sm, four from md. Empty, the hint sits centred in
                                    // the same space instead.
                                    ...(candidates.length === 0
                                        ? {
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                          }
                                        : {
                                              display: 'grid',
                                              gridTemplateColumns: {
                                                  xs: 'repeat(2, minmax(0, 1fr))',
                                                  sm: 'repeat(3, minmax(0, 1fr))',
                                                  md: 'repeat(4, minmax(0, 1fr))'
                                              },
                                              alignContent: 'start',
                                              gap: 1.5
                                          })
                                }}
                            >
                                {candidates.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        <FormattedMessage
                                            id={
                                                source
                                                    ? 'media.link.none'
                                                    : 'media.link.pickSource'
                                            }
                                        />
                                    </Typography>
                                ) : (
                                    candidates.map((candidate) => (
                                        <CandidateTile
                                            key={candidate.media.id}
                                            candidate={candidate}
                                            checked={selected.includes(
                                                candidate.media.id
                                            )}
                                            onToggle={() =>
                                                toggle(candidate.media.id)
                                            }
                                        />
                                    ))
                                )}
                            </Box>
                        </FormSection>
                    </Stack>
                </Box>
                <DialogFooterBar>
                    <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={close}
                    >
                        <FormattedMessage id="common.cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddLinkIcon />}
                        disabled={selected.length === 0 || saving}
                        onClick={() => void confirm()}
                    >
                        <FormattedMessage
                            id="media.link.confirm"
                            values={{ count: selected.length }}
                        />
                    </Button>
                </DialogFooterBar>
            </Box>
        </FormDialog>
    );
}

function CandidateTile({
    candidate,
    checked,
    onToggle
}: {
    candidate: Candidate;
    checked: boolean;
    onToggle: () => void;
}) {
    const { media, placeLabel } = candidate;
    const { url, loading } = useMediaThumbUrl(media.id, media.updatedAt);
    return (
        <Card
            variant="outlined"
            sx={{
                minWidth: 0,
                borderColor: checked ? 'primary.main' : undefined,
                outline: checked ? '1px solid' : undefined,
                outlineColor: 'primary.main'
            }}
        >
            <CardActionArea onClick={onToggle}>
                <Box sx={{ position: 'relative' }}>
                    {url ? (
                        <CardMedia
                            component="img"
                            image={url}
                            alt={media.caption || media.originalName}
                            sx={{ height: TILE_HEIGHT, objectFit: 'cover' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: TILE_HEIGHT,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'action.hover'
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <BrokenImageIcon color="disabled" />
                            )}
                        </Box>
                    )}
                    {media.kind === 'sketch' && (
                        <GestureIcon
                            fontSize="small"
                            sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                color: 'common.white',
                                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.6))'
                            }}
                        />
                    )}
                    <Checkbox
                        checked={checked}
                        tabIndex={-1}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bgcolor: 'background.paper',
                            borderRadius: 0,
                            borderBottomLeftRadius: 4,
                            pointerEvents: 'none',
                            '&:hover': { bgcolor: 'background.paper' }
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        px: 1,
                        py: 0.75,
                        height: TILE_LABEL_HEIGHT,
                        boxSizing: 'border-box'
                    }}
                >
                    <Typography variant="body2" noWrap>
                        {media.caption || media.originalName}
                    </Typography>
                    <Typography
                        variant="caption"
                        noWrap
                        component="div"
                        sx={{ color: 'text.secondary' }}
                    >
                        {placeLabel}
                    </Typography>
                </Box>
            </CardActionArea>
        </Card>
    );
}
